import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Check, Languages } from "lucide-react";
import useLanguage from "../hooks/useLanguage";
import usePrefersReducedMotion from "../hooks/usePrefersReducedMotion";

export default function LanguageSwitcher() {
  const { language, setLanguage, supportedLanguages } = useLanguage();
  const [open, setOpen] = useState(false);
  const reducedMotion = usePrefersReducedMotion();
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label="Change language"
        aria-haspopup="menu"
        aria-expanded={open}
        className="flex items-center gap-1.5 rounded-full p-2 text-foam-50 transition-colors hover:bg-white/10 lg:px-3"
      >
        <Languages className="h-5 w-5 lg:h-4 lg:w-4" />
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            role="menu"
            aria-label="Select language"
            initial={reducedMotion ? false : { opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: reducedMotion ? 0 : 0.15 }}
            className="absolute right-0 top-full mt-2 w-40 overflow-hidden rounded-2xl bg-ocean-950 py-1.5 shadow-lg ring-1 ring-white/10"
          >
            {supportedLanguages.map((l) => (
              <button
                key={l.code}
                type="button"
                role="menuitemradio"
                aria-checked={language === l.code}
                onClick={() => {
                  setLanguage(l.code);
                  setOpen(false);
                }}
                className="flex w-full items-center justify-between px-4 py-2 text-left text-sm text-foam-100/80 transition-colors hover:bg-white/10 hover:text-foam-50"
              >
                {l.label}
                {language === l.code && <Check className="h-3.5 w-3.5 text-teal-300" />}
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
